/**
 * กฎเอกสารตามอายุงาน — "เมื่อไรต้องออก/ต้องเซ็นเอกสารฉบับไหนอีก"
 *
 * ⭐ ทุกอย่างในไฟล์นี้ **ตั้งค่าได้เองจากหน้าจอทั้งหมด** ไม่มีตัวเลขวันไหน hardcode
 * ในโค้ด (พี่ปุ้ยสั่ง 4 ส.ค. 2569: "อยากให้ตั้งค่าได้เองทั้งหมดเลย") — ที่อยู่ในนี้
 * เป็นแค่ **ค่าเริ่มต้นตอน seed ครั้งแรก** ซึ่ง HR แก้ทับได้ทุกตัว
 *
 * โมเดล: 1 กฎ = "นับจากอะไร (anchor) + บวก/ลบกี่วัน (offset) + เตือนล่วงหน้ากี่วัน"
 * แยกจากเช็คลิสต์รับพนักงานใหม่ ซึ่งดูแลเฉพาะช่วงแรกเข้า — อันนี้ดูแลตลอดอายุงาน
 */

/** จุดตั้งต้นที่ใช้นับวันครบกำหนด */
export const DOC_RULE_ANCHORS = [
  'start_date',      // วันเริ่มงาน
  'probation_end',   // วันครบทดลองงาน (คำนวณจากวันเริ่มงาน + จำนวนวันทดลองงาน)
  'contract_end',    // วันสิ้นสุดสัญญา (เฉพาะสัญญามีกำหนดระยะเวลา)
] as const;
export type DocRuleAnchor = (typeof DOC_RULE_ANCHORS)[number];

export const DOC_RULE_ANCHOR_LABELS: Record<DocRuleAnchor, string> = {
  start_date: 'วันเริ่มงาน',
  probation_end: 'วันครบทดลองงาน',
  contract_end: 'วันสิ้นสุดสัญญา',
};

/** ประเภทสัญญาจ้าง — ตัวกำหนดว่ากฎที่อิง contract_end จะทำงานไหม */
export const CONTRACT_TYPES = ['indefinite', 'fixed_term'] as const;
export type ContractType = (typeof CONTRACT_TYPES)[number];
export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  indefinite: 'ไม่มีกำหนดระยะเวลา (จ้างประจำ)',
  fixed_term: 'มีกำหนดระยะเวลา (ต้องต่อสัญญา)',
};

export interface DocumentRule {
  rule_key: string;
  label: string;
  /** ประเภทเอกสารที่ต้องออก (null = แค่เตือนให้ทำอะไรบางอย่าง ไม่ผูกเอกสาร) */
  doc_type?: string | null;
  anchor: DocRuleAnchor;
  /** บวก/ลบจากวันตั้งต้น — ลบ = ก่อนถึงวันนั้น (เช่น −30 = ล่วงหน้า 30 วันก่อนสัญญาหมด) */
  offset_days: number;
  /** ขึ้นในรายการ "ถึงกำหนด" ล่วงหน้ากี่วัน */
  warn_days: number;
  required: boolean;
  active?: boolean;
  sort_order: number;
  note?: string | null;
}

/**
 * ค่าเริ่มต้นตอน seed — **HR แก้ได้ทุกตัว ลบได้ เพิ่มเองได้**
 * ⚠️ ตัวเลขทดลองงานไม่ได้ฝังในกฎ — กฎอิง `probation_end` ซึ่งคำนวณจาก
 * "จำนวนวันทดลองงาน" ที่ตั้งไว้ระดับบริษัทหรือรายคน (ดู resolveProbationDays)
 */
export const DEFAULT_DOCUMENT_RULES: DocumentRule[] = [
  {
    rule_key: 'probation_review',
    label: 'ประเมินผลทดลองงาน',
    doc_type: null,
    anchor: 'probation_end',
    offset_days: -14,
    warn_days: 14,
    required: true,
    sort_order: 10,
    note: 'ต้องประเมินก่อนครบกำหนด — เลยวันแล้วถือว่าผ่านทดลองงานโดยปริยาย',
  },
  {
    rule_key: 'probation_result_letter',
    label: 'ออกหนังสือผลทดลองงาน',
    doc_type: 'other',
    anchor: 'probation_end',
    offset_days: 0,
    warn_days: 7,
    required: false,
    sort_order: 20,
    note: null,
  },
  {
    rule_key: 'contract_renewal',
    label: 'ต่อสัญญาจ้าง',
    doc_type: 'employment_contract',
    anchor: 'contract_end',
    offset_days: -30,
    warn_days: 30,
    required: true,
    sort_order: 30,
    note: 'ใช้เฉพาะพนักงานที่เป็นสัญญามีกำหนดระยะเวลา',
  },
];

/** จำนวนวันทดลองงานที่ใช้จริงกับพนักงานคนนี้ — รายคนชนะค่าบริษัทเสมอ */
export function resolveProbationDays(
  employeeDays: number | null | undefined,
  tenantDefaultDays: number | null | undefined,
): number | null {
  if (employeeDays != null && Number.isFinite(employeeDays) && employeeDays > 0) return employeeDays;
  if (tenantDefaultDays != null && Number.isFinite(tenantDefaultDays) && tenantDefaultDays > 0) return tenantDefaultDays;
  return null;
}

export interface DocRuleAnchorDates {
  start_date?: string | null;
  /** วันสิ้นสุดสัญญา (null = สัญญาไม่มีกำหนด) */
  contract_end_date?: string | null;
  contract_type?: ContractType | null;
  /** จำนวนวันทดลองงานที่ใช้จริง (จาก resolveProbationDays) */
  probation_days?: number | null;
}

function addDays(dateISO: string, days: number): string {
  const [y, m, d] = dateISO.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) + days * 86400000).toISOString().slice(0, 10);
}

/**
 * วันครบทดลองงาน = วันเริ่มงาน + จำนวนวันทดลองงาน − 1
 * (นับวันเริ่มงานเป็นวันที่ 1 — ทดลองงาน 119 วันเริ่ม 1 ม.ค. จะครบ 29 เม.ย.
 *  ไม่ใช่ 30 เม.ย. ซึ่งจะทำให้เกิน 119 วันไปหนึ่งวันโดยไม่ตั้งใจ)
 */
export function probationEndDate(startDate: string | null | undefined, probationDays: number | null | undefined): string | null {
  if (!startDate || probationDays == null || !Number.isFinite(probationDays) || probationDays <= 0) return null;
  return addDays(startDate, probationDays - 1);
}

/** วันตั้งต้นของกฎนี้สำหรับพนักงานคนนี้ — null = ใช้กฎนี้กับคนนี้ไม่ได้ */
export function anchorDateFor(anchor: DocRuleAnchor, e: DocRuleAnchorDates): string | null {
  switch (anchor) {
    case 'start_date':
      return e.start_date || null;
    case 'probation_end':
      return probationEndDate(e.start_date, e.probation_days);
    case 'contract_end':
      // สัญญาไม่มีกำหนด = ไม่มีวันหมดอายุ กฎกลุ่มนี้ไม่แตะเขาเลย
      if (e.contract_type === 'indefinite') return null;
      return e.contract_end_date || null;
  }
}

export type DocRuleState = 'not_applicable' | 'upcoming' | 'due_soon' | 'due_today' | 'overdue';

export interface DocRuleDue {
  state: DocRuleState;
  /** วันที่ต้องทำให้เสร็จ (null เมื่อใช้กฎนี้กับคนนี้ไม่ได้) */
  dueDate: string | null;
  daysLeft: number | null;
}

function daysBetween(a: string, b: string): number {
  const pa = a.slice(0, 10).split('-').map(Number);
  const pb = b.slice(0, 10).split('-').map(Number);
  return Math.round(
    (Date.UTC(pb[0] ?? 1970, (pb[1] ?? 1) - 1, pb[2] ?? 1) - Date.UTC(pa[0] ?? 1970, (pa[1] ?? 1) - 1, pa[2] ?? 1)) / 86400000,
  );
}

/**
 * กฎนี้ถึงกำหนดหรือยังสำหรับพนักงานคนนี้
 * ⚠️ `today` ส่งเข้ามาเสมอ ไม่เรียก new Date() เอง — เทสจะได้ไม่เน่าตามเวลา
 */
export function evaluateDocumentRule(
  rule: Pick<DocumentRule, 'anchor' | 'offset_days' | 'warn_days'>,
  emp: DocRuleAnchorDates,
  today: string,
): DocRuleDue {
  const anchorDate = anchorDateFor(rule.anchor, emp);
  if (!anchorDate) return { state: 'not_applicable', dueDate: null, daysLeft: null };

  const dueDate = addDays(anchorDate, rule.offset_days);
  const daysLeft = daysBetween(today, dueDate);
  const warn = Math.max(0, rule.warn_days);

  const state: DocRuleState =
    daysLeft < 0 ? 'overdue'
      : daysLeft === 0 ? 'due_today'
        : daysLeft <= warn ? 'due_soon'
          : 'upcoming';
  return { state, dueDate, daysLeft };
}

/** ด่วนกว่า = เลขน้อยกว่า (ใช้เรียงรายการ "ถึงกำหนด") */
export function docRuleUrgency(state: DocRuleState): number {
  switch (state) {
    case 'overdue': return 0;
    case 'due_today': return 1;
    case 'due_soon': return 2;
    case 'upcoming': return 3;
    case 'not_applicable': return 4;
  }
}
